import { useMutation } from '@apollo/client';
import { DialogTitle } from '@headlessui/react';
import { ShieldCheckIcon } from '@heroicons/react/24/outline';
import { Modal } from '@retainerclub/common';
import { CREATE_USER_CONSENT } from '@retainerclub/customer-api';
import { useAccount, useAuth, usePolicy } from '@retainerclub/hooks';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import {
  CONSENT_VERSION,
  GATE_INTRO,
  GATE_TITLE,
  MARKETING_LABEL,
  MarketingDescription,
  PREFERENCES_FOOTER,
  REQUIRED_BADGE,
  SELECT_ALL_LABEL,
  SMS_LABEL,
  SmsDescription,
  TERMS_LABEL,
  TermsDescription,
} from './ConsentCopy';
import { currentAccountUser, needsConsent } from './helpers/needsConsent';

// The gate cannot be backed out of; the modal is rendered non-dismissable.
function ignoreDismiss() {
  return undefined;
}

// Routes where the visitor is not yet (or no longer) a signed-in account
// holder, so no interstitial should appear.
const EXEMPT_PATH_PREFIXES = [
  '/onboarding',
  '/signin',
  '/forgot-password',
  '/reset-password',
  '/new-password',
];

const OPTIONAL_CONSENTS = [
  { name: 'acceptSms', label: SMS_LABEL, Description: SmsDescription },
  {
    name: 'acceptMarketing',
    label: MARKETING_LABEL,
    Description: MarketingDescription,
  },
];

const CHECKBOX_CLASS =
  'mt-1 size-4 rounded border-rcgray-600 text-rcprimary-400 focus:ring-rcprimary-400';

// Consent interstitial: an account holder invited by the primary holder, or
// one created before consent was captured, has no terms acceptance on
// record. Nothing on the platform is usable until they accept; the
// acceptance is written to the users columns and the account_events ledger
// with the request context, exactly like the onboarding form does.
function AccountConsentGate() {
  const { currentUser } = useAuth();
  const { account, accountRefetch } = useAccount();
  const { pathname } = useLocation();
  const { policyModalOpen, setPolicyAndOpenModal } = usePolicy();
  const accountUser = currentAccountUser(account, currentUser);
  const [consents, setConsents] = useState({
    acceptTerms: false,
    acceptSms: false,
    acceptMarketing: false,
  });
  // True while the three boxes were all checked through "Select all" rather
  // than one by one; recorded on the ledger rows as evidence of how consent
  // was given.
  const [selectAllUsed, setSelectAllUsed] = useState(false);

  // Start from what is already on record so submitting the gate never
  // silently withdraws an SMS or marketing consent the holder gave before.
  useEffect(() => {
    if (!accountUser) {
      return;
    }
    setConsents((current) => ({
      ...current,
      acceptSms: accountUser.acceptSms === true,
      acceptMarketing: accountUser.acceptMarketing === true,
    }));
  }, [accountUser]);
  const [errorMessages, setErrorMessages] = useState(null);
  const [createUserConsent, { loading }] = useMutation(CREATE_USER_CONSENT);

  const exempt = EXEMPT_PATH_PREFIXES.some((prefix) =>
    pathname.startsWith(prefix),
  );

  // Same reason as SubscriptionAcknowledgmentGate: the account query runs
  // once per page load, so re-check the holder's consent on every route
  // change (skipping the initial render, which the query already covers).
  const lastCheckedPath = useRef(pathname);
  useEffect(() => {
    if (lastCheckedPath.current === pathname) {
      return;
    }
    lastCheckedPath.current = pathname;
    if (currentUser && !exempt) {
      accountRefetch?.();
    }
  }, [pathname, currentUser, exempt, accountRefetch]);

  if (exempt || !currentUser || !needsConsent(account, currentUser)) {
    return null;
  }

  const allChecked =
    consents.acceptTerms && consents.acceptSms && consents.acceptMarketing;

  function toggle(name) {
    setSelectAllUsed(false);
    setConsents((current) => ({ ...current, [name]: !current[name] }));
  }

  function toggleAll() {
    const next = !allChecked;
    setSelectAllUsed(next);
    setConsents({ acceptTerms: next, acceptSms: next, acceptMarketing: next });
  }

  async function handleSubmit() {
    setErrorMessages(null);

    try {
      const { data } = await createUserConsent({
        variables: {
          input: {
            input: {
              ...consents,
              consentVersion: CONSENT_VERSION,
              selectAll: selectAllUsed && allChecked,
            },
            clientMutationId: crypto.randomUUID(),
          },
        },
      });

      const payload = data?.createUserConsent;
      const errors = payload?.errorMessages || [];
      if (errors.length > 0 || payload?.user?.acceptTerms !== true) {
        setErrorMessages(
          errors.length > 0
            ? errors
            : [
                {
                  path: 'base',
                  message: 'We could not save your consent. Please try again.',
                },
              ],
        );
        return;
      }

      await accountRefetch?.();
    } catch (_error) {
      setErrorMessages([
        {
          path: 'base',
          message: 'We could not save your consent. Please try again.',
        },
      ]);
    }
  }

  const canContinue = consents.acceptTerms && !loading;

  // The legal modal is a separate Headless UI dialog; while it is open this
  // one steps aside so the policy text is readable and clickable.
  return (
    <Modal
      open={!policyModalOpen}
      setOpen={ignoreDismiss}
      size="2xl"
      showCloseButton={false}
      zdex={950}
    >
      <div className="mx-auto flex max-w-xl flex-col items-center text-center">
        <ShieldCheckIcon className="h-12 w-12 text-rcprimary-400" />
        <DialogTitle
          as="h2"
          className="mt-4 font-semibold text-2xl text-gray-950 sm:text-3xl"
        >
          {GATE_TITLE}
        </DialogTitle>
        <p className="mt-3 text-gray-900 text-lg">{GATE_INTRO}</p>
      </div>

      <div className="mt-8 flex flex-col text-left">
        <label
          className="flex gap-x-3 rounded-t-lg border border-gray-200 bg-gray-50 px-4 py-3"
          htmlFor="account-consent-all"
        >
          <input
            id="account-consent-all"
            type="checkbox"
            className={CHECKBOX_CLASS}
            checked={allChecked}
            onChange={toggleAll}
          />
          <span className="font-semibold text-black/90 text-sm leading-6">
            {SELECT_ALL_LABEL}
          </span>
        </label>

        <div className="flex flex-col gap-4 rounded-b-lg border border-gray-200 border-t-0 p-4">
          <label
            className="flex gap-x-3"
            htmlFor="account-consent-acceptTerms"
          >
            <input
              id="account-consent-acceptTerms"
              type="checkbox"
              className={CHECKBOX_CLASS}
              checked={consents.acceptTerms}
              onChange={() => toggle('acceptTerms')}
            />
            <span className="text-sm leading-6">
              <span className="flex items-center gap-2 font-medium text-black/90">
                {TERMS_LABEL}
                <span className="rounded-full bg-gray-200 px-2 py-0.5 font-semibold text-[10px] text-gray-700 uppercase tracking-wide">
                  {REQUIRED_BADGE}
                </span>
              </span>
              <span className="text-black/60 text-xs">
                <TermsDescription onOpenPolicy={setPolicyAndOpenModal} />
              </span>
            </span>
          </label>

          {OPTIONAL_CONSENTS.map(({ name, label, Description }) => (
            <label
              key={name}
              className="flex gap-x-3"
              htmlFor={`account-consent-${name}`}
            >
              <input
                id={`account-consent-${name}`}
                type="checkbox"
                className={CHECKBOX_CLASS}
                checked={consents[name]}
                onChange={() => toggle(name)}
              />
              <span className="text-sm leading-6">
                <span className="block font-medium text-black/90">{label}</span>
                <span className="text-black/60 text-xs">
                  <Description />
                </span>
              </span>
            </label>
          ))}
        </div>
      </div>

      {errorMessages && (
        <ul className="mt-4 text-red-600 text-sm">
          {errorMessages.map((error) => (
            <li key={`${error.path}-${error.message}`}>{error.message}</li>
          ))}
        </ul>
      )}

      <div className="mt-8 flex flex-col items-stretch gap-3">
        <button
          type="button"
          className="inline-flex min-h-12 items-center justify-center rounded-lg bg-rcprimary-400 px-6 font-medium text-gray-950 text-xl hover:bg-rcprimary-300 focus-visible:outline-2 focus-visible:outline-rcprimary-400 focus-visible:outline-offset-2 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-500"
          disabled={!canContinue}
          onClick={handleSubmit}
        >
          {loading ? 'Saving...' : 'Save & Continue'}
        </button>
        <p className="text-center text-gray-500 text-xs">{PREFERENCES_FOOTER}</p>
      </div>
    </Modal>
  );
}

export default AccountConsentGate;
